import { useEffect, useRef } from 'react'

/**
 * Avisa quando esta janela fica parada por `minutos`.
 *
 * Quem decide e o CLIENTE, porque so ele ve mouse e teclado — o servidor
 * jamais saberia diferenciar "parado" de "conectado e quieto".
 *
 * A contagem e por JANELA, nao por pessoa: quem deixou o PC parado mas esta
 * mexendo no celular continua disponivel, porque o servidor so considera
 * ausente quando TODAS as sessoes estao paradas.
 *
 * O tempo vem da configuracao da instalacao (Console > Configuracoes), nao de
 * constante: o admin escolhe entre 1 e 60 minutos.
 */

/**
 * Eventos que contam como "a pessoa esta aqui".
 *
 * `visibilitychange` entra porque minimizar a janela ou trocar de aba nao gera
 * mouse nem teclado, e voltar para ela e sinal claro de presenca.
 */
const SINAIS_DE_ATIVIDADE = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
  'focus',
] as const

export function useOciosidade(aoMudar: (ocioso: boolean) => void, minutos: number) {
  // `aoMudar` costuma ser arrow recriada a cada render; no ref ela nao
  // reinstala os ouvintes a cada digito digitado. Precisa ser `useRef` de
  // verdade: um objeto solto seria recriado a cada render e o efeito, que roda
  // uma vez so, ficaria preso ao primeiro.
  const aoMudarRef = useRef(aoMudar)
  aoMudarRef.current = aoMudar

  useEffect(() => {
    let ocioso = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const marcar = (valor: boolean) => {
      if (ocioso === valor) return
      ocioso = valor
      aoMudarRef.current(valor)
    }

    const rearmar = () => {
      marcar(false)
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(() => marcar(true), minutos * 60 * 1000)
    }

    const aoVisibilidade = () => {
      // Aba escondida nao recebe mousemove: a contagem continua correndo, e
      // voltar para ela reinicia. Sem isto, quem minimiza por 20 min e volta
      // apareceria ausente ate mexer o mouse.
      if (!document.hidden) rearmar()
    }

    for (const evento of SINAIS_DE_ATIVIDADE) {
      window.addEventListener(evento, rearmar, { passive: true })
    }
    document.addEventListener('visibilitychange', aoVisibilidade)
    rearmar()

    return () => {
      for (const evento of SINAIS_DE_ATIVIDADE) window.removeEventListener(evento, rearmar)
      document.removeEventListener('visibilitychange', aoVisibilidade)
      if (timer !== null) clearTimeout(timer)
    }
    // `minutos` NAS DEPS: quando o super admin muda o parametro no Console, o
    // relogio precisa reiniciar com o valor novo. Sem isto, quem esta com o app
    // aberto continuaria no tempo antigo ate recarregar.
  }, [minutos])
}
